<template>
  <div class="page-wrap" :class="{ night }">
    <AppHeader />

    <main class="page-body">
      <div class="page-top">
        <div class="ticker">
          <img class="ticker-horn" :src="hornIcon" alt="" />
          <div class="ticker-mask">
            <div class="ticker-track">
              <span v-for="(item, i) in tickerLoop" :key="i" class="ticker-item" v-html="formatTicker(item)" />
            </div>
          </div>
        </div>

        <div class="cat-pills">
          <button
            v-for="tab in catTabs"
            :key="tab.key"
            class="pill"
            :class="{ active: activeCat === tab.key }"
            type="button"
            @click="activeCat = tab.key"
          >
            {{ tab.label }}
          </button>
        </div>
      </div>

      <!-- 红框区域：仅此处上下滚动，上方顶栏/公告/分类固定 -->
      <div class="list-scroll">
        <template v-if="isFavTab">
          <div class="block-head">
            <h2>我的收藏</h2>
            <button class="edit-btn" type="button" @click="editing = !editing">
              <span class="edit-minus">−</span>
              {{ editing ? '完成' : '编辑' }}
            </button>
          </div>
          <div v-if="favGames.length" class="fav-grid">
            <div
              v-for="game in favGames"
              :key="game.id"
              class="fav-card"
              @click="goBet(game.id)"
            >
              <button
                v-if="editing"
                class="remove-btn"
                type="button"
                @click.stop="toggleFav(game)"
              >−</button>
              <img :src="game.icon" :alt="game.name" />
              <div class="fav-info">
                <div class="fav-name">{{ game.name }}</div>
                <div class="fav-period">{{ game.period }}</div>
              </div>
            </div>
          </div>
          <div v-else class="empty-tip">暂无收藏，去下面推荐里点红心吧</div>

          <h2 class="block-title">收藏推荐</h2>
          <ul class="game-list">
            <li v-for="game in recommendGames" :key="game.id">
              <GameRow :game="game" :show-players="false" />
            </li>
          </ul>
        </template>

        <template v-else>
          <h2 class="block-title">{{ currentTabLabel }}</h2>
          <ul class="game-list">
            <li v-for="game in filteredGames" :key="game.id">
              <GameRow :game="game" :show-players="false" />
            </li>
          </ul>
        </template>
      </div>
    </main>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AppHeader from '@/components/AppHeader.vue'
import GameRow from '@/components/GameRow.vue'
import { games, tickerList, toggleFav, visibleGames, isGameVisible, CATEGORY_GAME_IDS } from '@/data/games'
import hornIcon from '@/assets/lobby/ui/horn-circle.svg'
import { useTheme } from '@/composables/useTheme'
import { setActiveGame } from '@/composables/useActiveGame'
import { getBetPath } from '@/composables/betRoute'

const route = useRoute()
const router = useRouter()
const { night } = useTheme()
const editing = ref(false)

const catTabs = [
  { key: 'all', label: '全部游戏' },
  { key: 'fav', label: '我的收藏' },
  { key: 'LF', label: '低频彩' },
  { key: 'PK10', label: 'PK10' },
  { key: 'LHC', label: '六合彩' },
  { key: 'K3', label: '快三' },
  { key: 'KENO', label: '快乐8' }
]

const activeCat = ref(route.query.tab === 'fav' ? 'fav' : 'all')
const isFavTab = computed(() => activeCat.value === 'fav')
const currentTabLabel = computed(() => catTabs.find((t) => t.key === activeCat.value)?.label || '')

const filteredGames = computed(() => {
  const base = visibleGames.value
  if (activeCat.value === 'all') return base
  const ids = CATEGORY_GAME_IDS[activeCat.value]
  if (Array.isArray(ids) && ids.length) {
    return ids
      .map((id) => base.find((g) => Number(g.id) === Number(id)))
      .filter(Boolean)
  }
  return base.filter((g) => g.group === activeCat.value)
})

const favGames = computed(() => visibleGames.value.filter((g) => g.fav))
const recommendGames = computed(() =>
  visibleGames.value.filter((g) => !g.fav && (g.hot || g.badge)).slice(0, 4)
)

const goBet = (id) => {
  setActiveGame(id)
  router.push({ path: getBetPath(id), query: { id } })
}

const tickerLoop = [...tickerList, ...tickerList]

const formatTicker = (text) => text.replace(/(\d+U)/g, '<em>$1</em>')

watch(() => route.query.tab, (tab) => {
  if (tab === 'fav') activeCat.value = 'fav'
})

watch(activeCat, (key) => {
  if (key !== 'fav') editing.value = false
})
</script>

<style scoped>
/* 顶栏固定 + 中间列表滚动（红框内）；覆盖全局 bet-page-shell */
.page-wrap {
  position: relative;
  display: flex !important;
  flex-direction: column !important;
  box-sizing: border-box;
  width: 100%;
  max-width: 480px;
  height: 100vh !important;
  height: 100dvh !important;
  max-height: 100vh !important;
  max-height: 100dvh !important;
  margin: 0 auto;
  background: #f42c4c;
  font-family: 'PingFang SC', 'Microsoft YaHei', Avenir, Helvetica, Arial, sans-serif;
  color: #1a1a1a;
  overflow: hidden !important;
}

.page-wrap :deep(.app-chrome) {
  flex-shrink: 0;
}

.page-body {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 18px 18px 0 0;
  padding: 12px 12px 0;
  overflow: hidden;
}

.page-top {
  flex-shrink: 0;
}

.list-scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;
  padding-bottom: 28px;
}

.ticker {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 10px;
  background: #f7f7f9;
  border-radius: 99px;
  overflow: hidden;
}

.ticker-horn {
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  display: block;
}

.ticker-mask {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.ticker-track {
  display: flex;
  align-items: center;
  gap: 28px;
  width: max-content;
  animation: marquee 22s linear infinite;
}

.ticker-item {
  font-size: 12px;
  color: #666;
  white-space: nowrap;
}

.ticker-item :deep(em) {
  color: #f42c4c;
  font-style: normal;
  font-weight: 700;
}

@keyframes marquee {
  from { transform: translateX(0); }
  to { transform: translateX(-50%); }
}

.cat-pills {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 8px;
  margin: 14px 0 16px;
}

.pill {
  height: 32px;
  padding: 0 4px;
  border: 0;
  border-radius: 8px;
  background: #f3f4f6;
  color: #555;
  font-size: 12px;
  cursor: pointer;
  white-space: nowrap;
}

.pill.active {
  background: #f42c4c;
  color: #fff;
  font-weight: 700;
}

.block-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.block-head h2,
.block-title {
  margin: 0 0 10px;
  font-size: 16px;
  font-weight: 800;
}

.block-head h2 {
  margin: 0;
}

.edit-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 26px;
  padding: 0 10px 0 6px;
  border: 0;
  border-radius: 6px;
  background: #f42c4c;
  color: #fff;
  font-size: 13px;
  cursor: pointer;
}

.edit-minus {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #fff;
  color: #f42c4c;
  font-size: 14px;
  line-height: 15px;
  text-align: center;
  font-weight: 700;
}

.fav-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  margin-bottom: 18px;
}

.fav-card {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  padding: 12px 8px;
  border-bottom: 1px solid #f0f1f5;
  cursor: pointer;
}

.fav-card img {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  flex-shrink: 0;
}

.fav-info {
  min-width: 0;
  text-align: left;
}

.fav-name {
  font-size: 13px;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.fav-period {
  margin-top: 4px;
  font-size: 12px;
  color: #9aa0a8;
}

.remove-btn {
  position: absolute;
  top: 2px;
  right: 18px;
  width: 18px;
  height: 18px;
  border: 0;
  border-radius: 50%;
  background: #f42c4c;
  color: #fff;
  font-size: 16px;
  line-height: 16px;
  cursor: pointer;
}

.empty-tip {
  padding: 20px 0;
  text-align: center;
  color: #999;
  font-size: 13px;
}

.game-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.game-list li {
  border-bottom: 1px solid #f2f3f7;
}

.game-list li:last-child {
  border-bottom: 0;
}

.night .page-body {
  background: #2c2e35;
  color: #fff;
}

.night .ticker {
  background: #3a3d45;
}

.night .ticker-item {
  color: #ddd;
}

.night .pill {
  background: #3a3d45;
  color: #fff;
}

.night .pill.active {
  background: #f42c4c;
  color: #fff;
}

.night .block-head h2,
.night .block-title,
.night .fav-name {
  color: #fff;
}

.night .fav-grid,
.night .fav-card,
.night .game-list li {
  border-color: #3f424a;
}
</style>
